// eslint-disable-next-line no-redeclare
import _ from 'lodash';
// eslint-disable-next-line max-len
import { validateQuestion } from '@hotjar/webapp/features/Surveys/CreateEditSurvey/components/Question/questionValidation';
import { i18next as i18n } from '@hotjar/translations';

var generateUUID = require('uuid/v4');
/**
 * Model for polls.
 */
angular.module('hjApp').service('PollModel', [
    '$rootScope',
    '$timeout',
    'ModalPrompt',
    function ($rootScope, $timeout, ModalPrompt) {
        /**
         * Create a new instance of the poll model.
         *
         * @param data Model data (usually retrieved from the server).
         * @returns {Poll} A new Poll object.
         */
        this.create = function (data) {
            return new Poll(data);
        };

        /**
         * The actual poll model.
         */
        function Poll(data) {
            var self = this;

            // Extend the Poll model with its data.
            angular.extend(self, data);

            // Extend the Question model (sub-model of Poll) with its data.
            _.each(self.content.questions, function (questionData, i) {
                self.content.questions[i] = new Question(self, questionData);
            });

            /**
             * Add an open ended question to the poll.
             *
             * @param type Question type.
             * @param index Index of where the new question should be added.
             */
            self.addOpenEndedQuestion = function (type, index) {
                self.addQuestion(
                    {
                        type: type,
                        text: '',
                        next: 'byOrder'
                    },
                    index
                );
            };

            /**
             * Add an email question to the poll.
             *
             * @param index Index of where the new question should be added.
             */
            self.addEmailQuestion = function (index) {
                self.addQuestion(
                    {
                        type: 'email',
                        text: 'Please enter your email address:',
                        next: 'byOrder'
                    },
                    index
                );
            };

            /**
             * Add a close ended question to the poll.
             *
             * @param type Question type.
             * @param answersTemplate An answer template we can use to auto populate the question.
             * @param index Index of where the new question should be added.
             */
            self.addCloseEndedQuestion = function (type, answersTemplate, index) {
                self.addQuestion(
                    {
                        type: type,
                        text: '',
                        answers: answersTemplate,
                        next: 'byOrder',
                        randomize_answer_order: false
                    },
                    index
                );
            };

            /**
             * Add a scale question to the poll.
             *
             * @param type Question type.
             * @param scaleCount Total options in the scale.
             * @param index Index of where the new question should be added.
             */
            self.addScaleQuestion = function (type, scaleCount, index) {
                self.addQuestion(
                    {
                        type: type,
                        text: '',
                        labels: [{ text: 'Strongly disagree' }, { text: 'Strongly agree' }],
                        next: 'byOrder',
                        scaleCount: scaleCount
                    },
                    index
                );
            };

            /**
             * Add an NPS question to the poll.
             *
             * @param index Index of where the new question should be added.
             */
            self.addNPSQuestion = function (index) {
                self.addQuestion(
                    {
                        type: 'net-promoter-score',
                        text: i18n.t('feedback:wizard.section.questions.add-question.default-text.nps') ?? '',
                        labels: [{ text: 'Not likely at all' }, { text: 'Extremely likely' }],
                        next: 'byOrder'
                    },
                    index
                );
            };
            /**
             * Add an Reaction question to the poll.
             *
             * @param index Index of where the new question should be added.
             */
            self.addReactionQuestion = function (index) {
                self.addQuestion(
                    {
                        type: 'reaction',
                        text: i18n.t('feedback:wizard.section.questions.add-question.default-text.reaction') ?? '',
                        labels: [{ text: 'Not good at all' }, { text: 'Very good' }],
                        next: 'byOrder',
                        reaction_style: 'default'
                    },
                    index
                );
            };

            /**
             * Add an open ended question to the poll.
             *
             * @param type Question type.
             * @param index Index of where the new question should be added.
             */
            self.addTitleAndDescriptonQuestion = function (index) {
                self.addQuestion(
                    {
                        type: 'title-and-description',
                        text: '',
                        description: '',
                        next: 'byOrder'
                    },
                    index
                );
            };

            /**
             * Remove a question from the current poll using a confirmation prompt if needed.
             * NOTE: The prompt is only showed if the question is used in question logic.
             *
             * @param question Object to remove.
             * @param index Index of the question we are removing.
             */
            self.removeQuestionWithConfirmation = function (question) {
                var questionUsedInQuestionLogic = checkIfUsedInQuestionLogicAndReplaceWithDefault(
                    `question:${question.uuid}`
                );

                if (questionUsedInQuestionLogic) {
                    ModalPrompt.show(
                        'Are you sure?',
                        'Some questions using Question Logic are linking to this question when answered. ' +
                            'Removing this question will reset them to go to the next question in sequence instead. ' +
                            'Would you like to continue?',
                        'Yes, remove',
                        'Cancel',
                        function () {
                            // Before removing the question, remove its occurrences from question logic
                            checkIfUsedInQuestionLogicAndReplaceWithDefault(`question:${question.uuid}`, 'byOrder');
                            removeQuestion(question);
                        }
                    );
                } else {
                    // Confirmation not needed: Question is not linked by any other question.
                    removeQuestion(question);
                }
            };

            self.regenerateUuidForPollQuestions = function (questionUuids) {
                _.each(questionUuids, function (oldUuid) {
                    const matchingQuestion = _.findWhere(self.content.questions, { uuid: oldUuid });

                    if (matchingQuestion !== undefined) {
                        const newUuid = generateUUID();
                        matchingQuestion.uuid = newUuid;
                        checkIfUsedInQuestionLogicAndReplaceWithDefault(`question:${oldUuid}`, `question:${newUuid}`);
                    }
                });
            };

            /**
             * Add a new question to the poll.
             *
             * @param questionData Data making up the question content.
             * @param index Index of where the new question should be added.
             */
            self.addQuestion = function (questionData, index) {
                var question = new Question(self, questionData),
                    insertAtIndex = index === undefined ? self.content.questions.length : index;

                self.content.questions = [
                    ...self.content.questions.slice(0, insertAtIndex),
                    question,
                    ...self.content.questions.slice(insertAtIndex)
                ];

                $rootScope.$emit('addedQuestion', { index: index });
            };

            /**
             * Remove a question from the current poll.
             *
             * @param question Object to remove.
             */
            function removeQuestion(question) {
                self.content.questions = _.reject(self.content.questions, function (q) {
                    return q.uuid == question.uuid;
                });
                self.content.questions = [...self.content.questions];

                $rootScope.$emit('removedQuestion');
            }

            /**
             * Check if a value is used in Question Logic.
             * IF a defaultValue is not set, the function returns a boolean value.
             *
             * @param value Value to check.
             * @param defaultValue If set, the value is replaced by this.
             */
            function checkIfUsedInQuestionLogicAndReplaceWithDefault(value, defaultValue) {
                var isDeletingQuestionUsedInQuestionLogic = false;

                // Loop through the questions.
                _.each(self.content.questions, function (q) {
                    if (q.next === value) {
                        isDeletingQuestionUsedInQuestionLogic = true;

                        // Replace the value with the defaultValue if it is set.
                        if (defaultValue) {
                            q.next = defaultValue;
                        }
                    }
                    if (q.nextIfSkipped === value) {
                        isDeletingQuestionUsedInQuestionLogic = true;

                        // Replace the value with the defaultValue if it is set.
                        if (defaultValue) {
                            q.nextIfSkipped = defaultValue;
                        }
                    }
                    if (q.next === 'byAnswer') {
                        // Loop through the question's answers.
                        _.each(q.nextByAnswer, function (a, i) {
                            if (a === value) {
                                isDeletingQuestionUsedInQuestionLogic = true;

                                // Replace the value with the defaultValue if it is set.
                                if (defaultValue) {
                                    q.nextByAnswer[i] = defaultValue;
                                }
                            }
                        });
                    }
                });

                if (!defaultValue) {
                    return isDeletingQuestionUsedInQuestionLogic;
                }
            }
        }

        /**
         * The actual question model (sub-model of Poll).
         */
        function Question(poll, data) {
            var self = this,
                isValid = data.isValid || validateQuestion(data),
                uuid = generateUUID(); // Temporary unique UUID so we can track re-sorting.

            // Extend the Question model (sub-model of Poll) with its data.
            angular.extend(self, data);

            // Set required.
            self.required = typeof data.required === 'undefined' ? true : data.required;

            // Only create a uuid if we are creating a new question that doesn't already have a UUID
            if (!self.uuid) {
                /**
                 * Get the questions (temporary) UUID.
                 */
                window.defineProperty(
                    self,
                    'uuid',
                    function () {
                        return uuid;
                    },
                    function () {},
                    { enumerable: true }
                );
            }

            // This property will hold the validity state of each question. This is needed in order to
            // determine the Question's section validity state. This property will not be included
            // to the sent poll data.
            window.defineProperty(
                self,
                'isValid',
                function () {
                    return isValid;
                },
                function (value) {
                    isValid = value;
                }
            );
        }
    }
]);
